// HK-locale formatters used by mobile + admin. All pure (no side effects)
// and deterministic for a given input + system clock.
//
// MANDATORY DISPLAY RULE (sub-plan 03 line 96): every UI surface that
// renders a server-supplied timestamp MUST pipe the ISO string through
// `formatDateHK` (absolute) or `formatRelativeHK` (relative). Direct use
// of `new Date(iso).toLocaleString()` without an explicit
// `{ timeZone: "Asia/Hong_Kong" }` is FORBIDDEN — it falls back to device
// locale and silently shifts dates for users roaming outside HKT.

/** Currency formatter per sub-plan 03 line 70-74: input is an HKD AMOUNT
 *  in dollars (matches `jobs.price_amount numeric(10,2)` in sub-plan 02
 *  line 279), output is "HKD 320.50" plain-text format. Negative supported.
 *  Reconciliation note: an earlier draft used Intl.NumberFormat with cents
 *  input ("HK$100.50"); reverted to spec to match the DB storage unit and
 *  downstream report formats (admin A15/A16 commission/billing). */
export function formatHKD(amount: number): string {
  const sign = amount < 0 ? "-" : "";
  const abs = Math.abs(amount).toFixed(2);
  return `${sign}HKD ${abs}`;
}

/** Address parts → single display string in HK convention: apartment /
 *  floor first ("RM 20, 15/F"), then building, street, district, region.
 *  Empty / undefined parts are dropped. Returns "—" for an empty/null
 *  input so callers can use it directly in placeholder positions.
 *
 *  HK addresses are READ from most-specific (apt) to most-general (region)
 *  — putting "RM 20, 15/F" at the END (which postal-style does) makes the
 *  unit number easy to miss and is a complaint we keep getting. This
 *  helper is the single source of truth across mobile + admin so we don't
 *  drift again. */
export interface AddressParts {
  apt?: string | null;
  building?: string | null;
  street?: string | null;
  district?: string | null;
  region?: string | null;
}
export function formatAddressHK(a: AddressParts | null | undefined): string {
  if (!a) return "—";
  const parts = [a.apt, a.building, a.street, a.district, a.region]
    .map((p) => (p ?? "").trim())
    .filter((p) => p.length > 0);
  return parts.length > 0 ? parts.join(", ") : "—";
}

/** Phone display formatter: E.164 "+85255551234" → "5555 1234". Returns the
 *  input unchanged if it doesn't match the +852 + 8-digit pattern. */
export function formatPhoneHK(e164: string): string {
  const m = e164.match(/^\+852(\d{4})(\d{4})$/);
  return m ? `${m[1]} ${m[2]}` : e164;
}

/** Phone normalizer: accepts "+852 5555 1234", "5555-1234", "85255551234",
 *  "+85255551234" etc. Returns canonical E.164 "+85255551234" (8 HK digits
 *  prefixed with +852). Throws "invalid_hk_phone" on anything else. */
export function normalizePhoneHK(input: string): string {
  const digits = input.replace(/\D/g, "");
  if (digits.length === 8) return `+852${digits}`;
  if (digits.startsWith("852") && digits.length === 11) return `+${digits}`;
  throw new Error("invalid_hk_phone");
}

/** Boolean variant of normalizePhoneHK: returns true iff the input parses to
 *  a valid HK E.164 number (+852 + 8 digits). Wraps `normalizePhoneHK` so
 *  the validation rule stays single-sourced. */
export function validatePhoneHK(input: string): boolean {
  try {
    const e164 = normalizePhoneHK(input);
    return /^\+852\d{8}$/.test(e164);
  } catch {
    return false;
  }
}

/** Reviewer-name masking helper for the M14 engineer-profile reviews list
 *  (sub-plan 06 §6.6 + sub-plan 05 §5.x). Hides personal data per HK PDPO:
 *  Western/space-delimited names → "Alice C." (first name + last initial);
 *  single-token names (Chinese/single-word) → "張小*" (last code-point masked).
 *  Uses Array.from for correct surrogate-pair handling. Empty/missing → "—". */
export function maskName(name: string): string {
  const trimmed = (name ?? "").trim();
  if (!trimmed) return "—";
  const parts = trimmed.split(/\s+/);
  if (parts.length >= 2 && parts[0] && parts[1]) {
    return `${parts[0]} ${parts[1][0]}.`;
  }
  const chars = Array.from(trimmed);
  if (chars.length === 1) return chars[0] ?? "—";
  return chars.slice(0, -1).join("") + "*";
}

/** Date formatter: ISO UTC → "YYYY-MM-DD HH:mm" rendered in Asia/Hong_Kong.
 *  Uses en-HK locale + explicit timeZone option (NEVER relies on device tz). */
export function formatDateHK(iso: string): string {
  return new Date(iso).toLocaleString("en-HK", {
    timeZone: "Asia/Hong_Kong",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

/** Relative-time formatter: "just now" / "X minutes/hours/days ago" /
 *  falls back to formatDateHK after 7 days. Anchored to HKT for parity
 *  with formatDateHK so cross-references in the UI stay consistent.
 *  Used by notification timestamps (E14, M27) and feed cards. */
export function formatRelativeHK(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  const diffMs = now.getTime() - d.getTime();
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min} minute${min === 1 ? "" : "s"} ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hour${hr === 1 ? "" : "s"} ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day} day${day === 1 ? "" : "s"} ago`;
  return formatDateHK(iso);
}
